<template>
  <div>
    <v-navigation-drawer
      v-if="isAuthenticated && !isClient"
      id="sc-primary-nav-drawer"
      :model-value="drawerVisible"
      class="c-side-bar"
      theme="darkChrome"
      overflow
      :rail="$vuetify.display.smAndUp && isMini"
      :permanent="$vuetify.display.smAndUp"
      @update:model-value="setNavState"
    >
      <v-list class="mb-5">
        <v-tooltip :disabled="!isMini || $route.name === 'dashboard'" location="right">
          <template #activator="{ props }">
            <!-- The logo links to the dashboard, but only the Dashboard item
                 below should read as the current page there. -->
            <!-- BETA-CLEANUP: drop the beta "New" chips and `logo-link--new` in this row -->
            <v-list-item
              link
              class="logo-link pl-3"
              :class="{ 'logo-link--new': isBetaApp && isMini }"
              :active="false"
              :lines="isMini ? undefined : 'two'"
              :to="isMini ? getAccountRoute({ name: 'dashboard' }) : undefined"
              v-bind="props"
              @click="hideNavBar"
            >
              <template #prepend>
                <!-- the rail hides the title and its chip, so the beta's "New" sits under the logo there -->
                <div class="logo-link__mark">
                  <v-avatar size="30">
                    <v-img rounded="0" src="/img/sc-logo.png" :cover="false" />
                  </v-avatar>
                  <CommonChip
                    v-if="isBetaApp && isMini"
                    size="x-small"
                    color="#6CC04A"
                    variant="tonal"
                    class="logo-link__new"
                    data-test="beta-chip"
                  >
                    New
                  </CommonChip>
                </div>
              </template>
              <v-list-item-title class="overline" style="color: #6CC04A;">SlickCactus</v-list-item-title>
              <template v-if="isBetaApp && !isMini" #append>
                <CommonChip
                  x-small
                  color="#6CC04A"
                  variant="tonal"
                  data-test="beta-chip"
                >
                  New
                </CommonChip>
              </template>
            </v-list-item>
          </template>
          <span class="overline" style="font-size: 1.2em; font-weight: 500;">Dashboard</span>
        </v-tooltip>
        <v-list>
          <div v-for="link in items" :key="link.icon">
            <v-tooltip v-if="isMini && $route.name !== link.route && `/${$route.name}` !== `${link.route}`" location="right">
              <template #activator="{ props }">
                <v-list-item
                  class="side-bar-nav-list-item"
                  link
                  exact-path
                  :to="getAccountRoute({ name: link.route })"
                  v-bind="props"
                  @click="hideNavBar()"
                >
                  <template #prepend>
                    <v-icon>{{ link.icon }}</v-icon>
                  </template>
                  <v-list-item-title>{{ link.text }}</v-list-item-title>
                </v-list-item>
              </template>
              <span class="overline" style="font-size: 1.2em; font-weight: 500;">{{ link.text }}</span>
            </v-tooltip>
            <v-list-item
              v-else
              :to="getAccountRoute({ name: link.route })"
              class="side-bar-nav-list-item"
              link
            >
              <template #prepend>
                <v-icon>{{ link.icon }}</v-icon>
              </template>
              <v-list-item-title>{{ link.text }}</v-list-item-title>
            </v-list-item>
          </div>
        </v-list>
        <v-list v-if="customNavigationLinks && customNavigationLinks.length">
          <v-divider />
          <div v-for="(customNavigationLink, index) in customNavigationLinks" :key="index">
            <v-tooltip v-if="isMini" location="right">
              <template #activator="{ props }">
                <v-list-item
                  class="side-bar-nav-list-item"
                  target="_blank"
                  :href="customNavigationLink.url"
                  v-bind="props"
                >
                  <template #prepend>
                    <v-icon>{{ customNavigationLink.icon }}</v-icon>
                  </template>
                  <v-list-item-title>
                    {{ customNavigationLink.title }}
                  </v-list-item-title>
                </v-list-item>
              </template>
              <span class="overline" style="font-size: 1.2em; font-weight: 500;">{{ customNavigationLink.title }}</span>
            </v-tooltip>
            <v-list-item
              v-else
              class="side-bar-nav-list-item"
              target="_blank"
              :href="customNavigationLink.url"
            >
              <template #prepend>
                <v-icon>{{ customNavigationLink.icon }}</v-icon>
              </template>
              <v-list-item-title>{{ customNavigationLink.title }}</v-list-item-title>
            </v-list-item>
          </div>
        </v-list>
      </v-list>
      <template #append>
        <div v-if="$vuetify.display.smAndUp" class="d-flex justify-center mb-2">
          <v-spacer v-if="!isMini" />
          <v-btn icon style=" opacity: .3" @click="updateNavView">
            <v-icon>mdi-chevron-double-{{ isMini ? 'right' : 'left' }}</v-icon>
          </v-btn>
        </div>
        <v-divider />
        <div v-if="$vuetify.display.xs || !isMini" class="pa-2 mt-0 text-center">
          <v-btn style="color: #999" data-test="sidebar-help" @click="openTrackerForm">
            <v-icon size="small" class="mr-2" style="color: #999">mdi-comment-question</v-icon>
            Support / Feedback
          </v-btn>
        </div>
        <v-list-item
          v-else
          class="user-actions-list-item"
          data-test="sidebar-help"
          @click="openTrackerForm"
        >
          <template #prepend>
            <v-icon style="color: #999">mdi-comment-question</v-icon>
          </template>
        </v-list-item>
      </template>
    </v-navigation-drawer>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex';

import { RootMutations } from '@/store/types/mutation-types';
import { AccountLayoutMixin } from '@/bundles/App/mixins/accountMixin';
import { usePlaceholders } from '@/bundles/App/composables/usePlaceholders';
import { useUserRoles } from '@/bundles/App/composables/useUserRoles';
import { isBetaApp } from '@/bundles/App/helpers/betaChannel'; // BETA-CLEANUP

import CommonChip from '@/bundles/Common/components/chips/CommonChip.vue';

export default {
  name: 'SideBar',

  components: { CommonChip },

  mixins: [AccountLayoutMixin],

  setup () {
    const { contactPlaceholder } = usePlaceholders();
    const { isClient } = useUserRoles();

    return { contactPlaceholder, isClient, isBetaApp };
  },

  data: () => ({
    drawer: false,
    showUserActions: false,
    mini: false,
    appBarWidth: 230,
    items: [
      { icon: 'mdi-view-dashboard-variant', text: 'Dashboard', route: 'dashboard' },
      { icon: 'mdi-map', text: 'Spatial GIS', route: 'spatial-search' },
      { icon: 'mdi-city', text: 'Companies', route: 'companies' },
      { icon: 'mdi-contacts', text: 'Contacts', route: 'contacts' },
      { icon: 'mdi-office-building-marker',
        text: 'Occupiers',
        route: 'occupiers',
      },
      { icon: 'mdi-check-all', text: 'Requirements', route: 'requirements' },
      { icon: 'mdi-domain', text: 'Properties', route: 'properties' },
      {
        icon: 'mdi-map-marker-radius',
        text: 'Availabilities',
        route: 'availabilities',
      },
      { icon: 'mdi-compare', text: 'Comparables', route: 'comparables' },
      { icon: 'mdi-clipboard-text', text: 'Presentations', route: 'projects' },
      { icon: 'mdi-table', text: 'Saved Sets', route: 'sets' },
      { icon: 'mdi-pipe', text: 'Pipelines', route: 'pipelines' },
      { icon: 'mdi-currency-usd', text: 'Closed Deals', route: 'deals' },
      { icon: 'mdi-flash-outline', text: 'Marketing', route: 'marketing' },
    ],
  }),

  computed: {
    ...mapGetters(['isAuthenticated']),
    ...mapState({
      userLogo: (state) => state.user?.profile?.img,
      nav: (state) => state.nav,
      isMini: (state) => state.isMiniNav,
      customNavigationLinks: (state) => state.accountSettings?.customNavigationLinks,
    }),
    navState: {
      get: function () {
        return this.nav;
      },
      set: function (value) {
        this.setNavState(value);
      }
    },
    // Vuetify 3 only self-activates a permanent drawer when `model-value` is
    // null, so a stored `false` would keep the rail hidden on desktop. Keep the
    // stored flag for the mobile overlay only.
    drawerVisible: function () {
      return this.$vuetify.display.smAndUp || this.nav;
    },
    userAvatar: function () {
      return this.userLogo || this.contactPlaceholder;
    },
  },

  methods: {
    ...mapMutations({
      setNavState: RootMutations.SET_NAV_STATE,
      setNavView: RootMutations.SET_NAV_VIEW,
    }),
    updateNavView: function () {
      this.setNavView(!this.isMini);
    },
    // the request form lives once, in App.vue, so error notifications can open it on any page too
    openTrackerForm: function () {
      this.drawer = false;
      this.$eventBus.$emit('open-request-form');
    },
    hideNavBar () {
      this.setNavState(false);
    },
  },
};
</script>

<style lang="scss" scoped>
// BETA-CLEANUP: the logo with the beta's "New" chip stacked under it in the rail (this rule and .logo-link__new)
.logo-link__mark {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

// a compact tag rather than a chip: the rail is 56px wide
.logo-link__new {
  height: 16px;
  padding: 0 6px;
  font-size: 0.5625rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

// The row keeps the top bar's 64px and centres the logo and tag in it. The app's list rhythm
// gives a row's leading element 12px above and below, which pushed the pair into Dashboard.
// (Scoped under the row: that rule is `.v-application .v-list-item__prepend > :not(...)`.)
.logo-link .v-list-item__prepend > .logo-link__mark {
  margin-top: 0;
  margin-bottom: 0;
}

// Vuetify always tags the active route's list-item with v-list-item--active
// (regardless of the active-class prop), so the logo row tints when the active
// route is the dashboard. It should not.
//
// The tint is `.v-list-item__overlay`, a real child element -- NOT `::before`,
// which this rule used to target. In Vuetify 3 `.v-list-item::before` computes
// `content: none`, so the old selector matched nothing and the logo went on
// tinting. Vuetify 2 did use a ::before overlay, which is where it came from.
.logo-link.v-list-item--active {
  .v-list-item__overlay {
    opacity: 0 !important;
  }
}
</style>
